import type { FormEvent, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { requestCodeSchema, verifyCodeSchema } from '@botguide/shared';

import { Button, ErrorNote, Field, Icon, Spinner } from '@/components';
import { ApiRequestError, authApi } from '@/lib/api';
import { cn } from '@/lib/cn';
import { pathAfterSignIn } from '@/lib/routes';
import { useSession } from '@/lib/session';

/** How long the resend button stays disabled, in seconds. */
const RESEND_COOLDOWN = 30;

type Step = 'email' | 'code';

interface LocationState {
  from?: { pathname?: string };
}

/**
 * Sign in. Two steps, no password.
 *
 * The API never says whether an address belongs to a user - `request-code`
 * returns 202 either way - so this screen must not imply it either. "We sent a
 * code to that address if it belongs to an account" is the honest wording and
 * it is also the one that stops the form being used as a staff roster.
 */
export default function SignIn() {
  const { status, user, refresh } = useSession();
  const location = useLocation();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [resent, setResent] = useState(false);
  const [expiresInMinutes, setExpiresInMinutes] = useState<number | null>(null);

  const codeInput = useRef<HTMLInputElement>(null);

  // Tick the resend cooldown down to zero.
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setTimeout(() => setCooldown((seconds) => seconds - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  // Moving to the code step should put the caret in the code box.
  useEffect(() => {
    if (step === 'code') codeInput.current?.focus();
  }, [step]);

  if (status === 'loading') {
    return (
      <Shell>
        <div className="grid place-items-center py-10">
          <Spinner size={22} />
        </div>
      </Shell>
    );
  }

  if (status === 'authenticated' && user) {
    const state = location.state as LocationState | null;
    // Restoring the remembered page is only right when this role can open it;
    // otherwise somebody's first screen is a refusal. `pathAfterSignIn` holds
    // that rule, and the sign-in page itself, next to the roles it depends on.
    return <Navigate to={pathAfterSignIn(state?.from?.pathname, user.role)} replace />;
  }

  async function sendCode(address: string, isResend: boolean) {
    const parsed = requestCodeSchema.safeParse({ email: address });
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message ?? 'Enter a valid email address.');
      return;
    }

    setBusy(true);
    setFieldError(null);
    setRequestError(null);
    try {
      const issued = await authApi.requestCode(parsed.data);
      setExpiresInMinutes(issued.expiresInMinutes ?? null);
      setEmail(parsed.data.email);
      setStep('code');
      setCooldown(RESEND_COOLDOWN);
      setResent(isResend);
    } catch (error) {
      // A 429 is the one failure this step can legitimately produce.
      setRequestError(error);
    } finally {
      setBusy(false);
    }
  }

  async function verify(event: FormEvent) {
    event.preventDefault();

    const parsed = verifyCodeSchema.safeParse({ email, code });
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message ?? 'Enter the six-digit code.');
      return;
    }

    setBusy(true);
    setFieldError(null);
    setRequestError(null);
    try {
      await authApi.verify(parsed.data);
      /*
       * The cookie is set. Refetch the session rather than trusting the body
       * we just received - the redirect below keys off `status`, so there is
       * exactly one place that decides where a signed-in person goes.
       */
      await refresh();
    } catch (error) {
      setCode('');
      codeInput.current?.focus();
      if (error instanceof ApiRequestError) {
        setFieldError(verifyMessage(error));
      } else {
        setRequestError(error);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell>
      {step === 'email' ? (
        <form
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            void sendCode(email, false);
          }}
        >
          <div>
            <h1 className="font-display text-title font-semibold tracking-[-0.02em]">Sign in</h1>
            <p className="mt-1.5 text-ui text-muted">
              We will email you a six-digit code. There is no password to remember.
            </p>
          </div>

          {requestError ? <ErrorNote error={requestError} /> : null}

          <Field
            label="Email address"
            type="email"
            name="email"
            inputMode="email"
            autoComplete="email"
            autoFocus
            required
            placeholder="you@company.com"
            value={email}
            error={fieldError}
            onChange={(event) => {
              setEmail(event.target.value);
              setFieldError(null);
            }}
          />

          <Button type="submit" variant="primary" size="lg" block loading={busy} iconRight="chevronRight">
            Send me a code
          </Button>
        </form>
      ) : (
        <form className="flex flex-col gap-5" onSubmit={(event) => void verify(event)}>
          <div>
            <button
              type="button"
              onClick={() => {
                setStep('email');
                setCode('');
                setFieldError(null);
                setRequestError(null);
                setResent(false);
              }}
              className="mb-3 inline-flex items-center gap-1 text-small text-muted transition-colors hover:text-ink"
            >
              <Icon name="chevronLeft" size={14} />
              Use a different address
            </button>

            <h1 className="font-display text-title font-semibold tracking-[-0.02em]">
              Check your email
            </h1>
            <p className="mt-1.5 text-ui text-muted">
              If <span className="font-medium text-ink">{email}</span> belongs to an account, a
              six-digit code is on its way.
              {expiresInMinutes === null
                ? ''
                : ` It expires in ${expiresInMinutes} minute${expiresInMinutes === 1 ? '' : 's'}.`}
            </p>
          </div>

          {resent ? (
            <p className="flex items-center gap-2 rounded-md border border-ok/25 bg-ok-soft px-3 py-2 text-small text-ok-ink">
              <Icon name="check" size={14} />A new code has been sent.
            </p>
          ) : null}

          {requestError ? <ErrorNote error={requestError} /> : null}

          <Field
            ref={codeInput}
            label="Six-digit code"
            name="code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            required
            mono
            placeholder="000000"
            className="text-center text-lead"
            value={code}
            error={fieldError}
            onChange={(event) => {
              // Strip anything that is not a digit as it is typed, so a pasted
              // "123 456" from an email client still works.
              setCode(event.target.value.replace(/\D/g, '').slice(0, 6));
              setFieldError(null);
            }}
          />

          <Button
            type="submit"
            variant="primary"
            size="lg"
            block
            loading={busy}
            disabled={code.length !== 6}
          >
            Sign in
          </Button>

          <div className="flex items-center justify-between gap-3 text-small">
            <span className="text-subtle">Did not arrive?</span>
            <button
              type="button"
              disabled={cooldown > 0 || busy}
              onClick={() => void sendCode(email, true)}
              className={cn(
                'font-medium transition-colors',
                cooldown > 0 || busy ? 'cursor-not-allowed text-subtle' : 'text-accent hover:underline',
              )}
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : 'Send another code'}
            </button>
          </div>
        </form>
      )}

      <DevOutboxHint />
    </Shell>
  );
}

/**
 * What a rejected code means.
 *
 * Two outcomes, and they need different next actions. Too many attempts means
 * stop for a moment; anything else means the code was wrong, expired or never
 * issued - and the API deliberately does not say which, because telling a
 * caller which one applies hands them a probing tool. So the server's own
 * message is preferred and the strings here are only a fallback.
 *
 * Either way the resend control below stays available, which is the action
 * that actually resolves both cases.
 */
function verifyMessage(error: ApiRequestError): string {
  if (error.isRateLimited) {
    return 'Too many attempts. Wait a minute, then request a new code.';
  }
  return error.message || 'That code is not valid. Request a new one.';
}

/* -------------------------------------------------------------------------- */

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="relative grid min-h-dvh place-items-center px-[var(--gutter)] py-10">
      <div className="paper-grain pointer-events-none fixed inset-0 -z-10" aria-hidden="true" />

      <div className="w-full max-w-[26rem] rise">
        <div className="mb-6 flex items-center gap-2.5">
          <span
            className="grid size-9 place-items-center rounded-lg bg-accent font-display text-lead font-bold leading-none text-on-accent"
            aria-hidden="true"
          >
            B
          </span>
          <span className="font-display text-section font-semibold tracking-tight">
            BotGuide<span className="text-subtle"> Portal</span>
          </span>
        </div>

        <div className="rounded-xl border border-line bg-surface p-6 shadow-float sm:p-7">{children}</div>

        <p className="mt-5 text-center text-micro text-subtle">
          Access is by invitation. Ask Ryan if you need an account.
        </p>
      </div>
    </div>
  );
}

/**
 * Development only.
 *
 * In development the API writes the email to an `outbox/` folder instead of
 * sending it, and `request-code` deliberately returns nothing useful in the
 * response - so without this note, the first thing a new person does on a
 * fresh clone is get stuck on this screen. Gated on `import.meta.env.DEV`, so
 * it is absent from the production bundle entirely.
 */
function DevOutboxHint() {
  if (!import.meta.env.DEV) return null;

  return (
    <aside className="mt-6 rounded-md border border-dashed border-warn/40 bg-warn-soft px-3.5 py-3 text-small text-warn-ink">
      <p className="flex items-center gap-1.5 font-semibold">
        <Icon name="key" size={13} />
        Development build
      </p>
      <p className="mt-1 leading-relaxed">
        No email is actually sent. The API writes it to{' '}
        <code className="rounded-xs bg-warn/15 px-1 py-0.5 font-mono">apps/api/outbox/</code> — open
        the newest <code className="font-mono">.eml</code> there and copy the six digits. It is also
        printed in the API's terminal.
      </p>
    </aside>
  );
}
